import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Container,
  Paper,
  Chip,
  Divider,
  IconButton,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Slide,
  Avatar,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  FlashOn as FlashOnIcon,
  Support as SupportIcon,
  TrendingUp as TrendingUpIcon,
  LocalOffer as LocalOfferIcon,
  Payment as PaymentIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SavedCreditCard, PaymentRequestData } from '../types/plan';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { creditApi } from '../utils/creditApi';
import { fetchPlanById } from '../utils/api';

const ComprarCreditosPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, currentOrganization } = useAuth();
  
  const [creditsAmount, setCreditsAmount] = useState<number>(100);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string>('professional');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [savedCards, setSavedCards] = useState<SavedCreditCard[]>([]);
  const [useNewCard, setUseNewCard] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [saveCard, setSaveCard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardHolder: '',
    expirationDate: '',
    securityCode: '',
    customerName: user?.name || '',
    customerEmail: user?.email || '',
    customerIdentity: '',
  });

  const creditPackages = [
    {
      id: 'basic',
      name: 'Starter',
      credits: 100,
      price: 29.90,
      originalPrice: 39.90,
      description: 'Perfeito para começar',
      features: ['100 créditos', 'Suporte por email', 'Acesso básico'],
      popular: false,
      icon: <FlashOnIcon />,
      color: '#4CAF50',
    },
    {
      id: 'professional',
      name: 'Professional',
      credits: 500,
      price: 129.90,
      originalPrice: 199.90,
      description: 'Ideal para profissionais',
      features: ['500 créditos', 'Suporte prioritário', 'Relatórios avançados', 'API access'],
      popular: true,
      icon: <TrendingUpIcon />,
      color: '#2196F3',
    },
    {
      id: 'business',
      name: 'Business',
      credits: 1000,
      price: 229.90,
      originalPrice: 399.90,
      description: 'Para empresas em crescimento',
      features: ['1000 créditos', 'Suporte 24/7', 'Analytics completos', 'Integrações', 'White-label'],
      popular: false,
      icon: <AutoAwesomeIcon />,
      color: '#9C27B0',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      credits: 2500,
      price: 499.90,
      originalPrice: 999.90,
      description: 'Solução empresarial completa',
      features: ['2500 créditos', 'Suporte dedicado', 'Customizações', 'SLA garantido', 'Treinamento'],
      popular: false,
      icon: <ShieldIcon />,
      color: '#FF9800',
    },
  ];

  useEffect(() => {
    if (user) {
      loadSavedCards();
      loadOrganizationData();
      setCardData(prev => ({
        ...prev,
        customerName: user.name || '',
        customerEmail: user.email || ''
      }));
    }
  }, [user]);

  const loadOrganizationData = async () => {
    try {
      // Determinar o plano atual baseado na organização
      if (currentOrganization?.currentPlan) {
        // Buscar detalhes do plano para verificar se é 'free'
        const planId = currentOrganization.currentPlan;
        try {
          const plan = await fetchPlanById(planId);
          
          // Verificar se o plano tem duration 'free'
          if (plan.duration === 'free') {
            setCurrentPlan('free');
          } else {
            setCurrentPlan('premium');
          }
        } catch (planError) {
          // Se não conseguir buscar o plano, assumir que não é free
          console.error('Erro ao buscar detalhes do plano:', planError);
          setCurrentPlan('premium');
        }
      } else {
        setCurrentPlan('free');
      }
    } catch (err) {
      console.error('Erro ao carregar dados da organização:', err);
      setCurrentPlan('free'); // Default para free em caso de erro
    }
  };

  const getCreditPrice = () => {
    // Regra de preço: R$ 0,20 para free, R$ 0,10 para outros planos
    return currentPlan === 'free' ? 0.20 : 0.10;
  };

  const loadSavedCards = async () => {
    try {
      const cards = await api.get<SavedCreditCard[]>('/Payment/GetSavedCards', { auth: true });
      setSavedCards(cards);
      
      // Se há cartões salvos, selecionar o padrão ou o primeiro
      if (cards.length > 0) {
        const defaultCard = cards.find((c: SavedCreditCard) => c.isDefault);
        const cardToSelect = defaultCard || cards[0];
        setSelectedCardId(cardToSelect.id);
        setUseNewCard(false); // Usar cartão salvo por padrão se existir
        
        // Preencher automaticamente os dados do cliente se disponíveis
        if (cardToSelect.customerCpfCnpj || cardToSelect.customerName || cardToSelect.customerEmail) {
          setCardData(prev => ({
            ...prev,
            customerIdentity: cardToSelect.customerCpfCnpj || prev.customerIdentity,
            customerName: cardToSelect.customerName || prev.customerName,
            customerEmail: cardToSelect.customerEmail || prev.customerEmail,
          }));
        }
      } else {
        setUseNewCard(true); // Usar novo cartão se não houver cartões salvos
      }
    } catch (error) {
      console.error('Erro ao carregar cartões salvos:', error);
      // Em caso de erro, usar novo cartão
      setUseNewCard(true);
    }
  };

  const handlePackageSelect = (packageData: typeof creditPackages[0]) => {
    setCreditsAmount(packageData.credits);
    setSelectedPackage(packageData.id);
  };

  const handleCustomAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    setCreditsAmount(value);
    setSelectedPackage('custom');
  };

  const handlePurchase = () => {
    setShowPaymentDialog(true);
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    return parts.length ? parts.join(' ') : v;
  };

  const formatExpirationDate = (value: string) => {
    const v = value.replace(/\D/g, '');
    if (v.length >= 2) {
      // Limitar mês a 12
      const month = Math.min(parseInt(v.substring(0, 2)) || 0, 12);
      const monthStr = month.toString().padStart(2, '0');
      if (v.length >= 4) {
        return monthStr + '/' + v.substring(2, 4);
      }
      return monthStr + (v.length > 2 ? '/' + v.substring(2) : '');
    }
    return v;
  };

  // Validação do número do cartão usando algoritmo de Luhn
  const validateCardNumber = (cardNumber: string): boolean => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (!cleaned || cleaned.length < 13 || cleaned.length > 19) {
      return false;
    }

    let sum = 0;
    let isEven = false;

    // Percorrer de trás para frente
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned.charAt(i));

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  // Validação da data de vencimento
  const validateExpirationDate = (expirationDate: string): { valid: boolean; message?: string } => {
    if (!expirationDate || expirationDate.length !== 5) {
      return { valid: false, message: 'Data de validade inválida' };
    }

    const parts = expirationDate.split('/');
    if (parts.length !== 2) {
      return { valid: false, message: 'Data de validade inválida' };
    }

    const month = parseInt(parts[0]);
    const year = parseInt(parts[1]);

    // Validar mês
    if (isNaN(month) || month < 1 || month > 12) {
      return { valid: false, message: 'Mês inválido. Use um valor entre 01 e 12' };
    }

    // Validar ano
    if (isNaN(year) || year < 0 || year > 99) {
      return { valid: false, message: 'Ano inválido' };
    }

    // Converter para data completa (assumindo 2000-2099)
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;

    // Converter ano de 2 dígitos para 4 dígitos
    const fullYear = year < 50 ? 2000 + year : 1900 + year;

    // Verificar se a data não está expirada
    if (fullYear < currentDate.getFullYear() || 
        (fullYear === currentDate.getFullYear() && month < currentMonth)) {
      return { valid: false, message: 'Cartão expirado. Verifique a data de vencimento' };
    }

    return { valid: true };
  };

  const formatCPF = (value: string) => {
    const v = value.replace(/\D/g, '');
    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;

    if (field === 'cardNumber') {
      formattedValue = formatCardNumber(value);
    } else if (field === 'expirationDate') {
      formattedValue = formatExpirationDate(value);
    } else if (field === 'customerIdentity') {
      formattedValue = formatCPF(value);
    }

    setCardData(prev => ({ ...prev, [field]: formattedValue }));
  };

  const validateCPF = (cpf: string): boolean => {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let rev = 11 - (sum % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(9))) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    rev = 11 - (sum % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(10))) return false;

    return true;
  };

  const validateForm = () => {
    if (!useNewCard && !selectedCardId) {
      setError('Selecione um cartão salvo ou use um novo cartão');
      return false;
    }

    if (useNewCard) {
      if (!cardData.cardNumber || !validateCardNumber(cardData.cardNumber)) {
        setError('Número do cartão inválido. Verifique o número do cartão');
        return false;
      }
      if (!cardData.cardHolder.trim()) {
        setError('Nome do portador é obrigatório');
        return false;
      }
      const expirationValidation = validateExpirationDate(cardData.expirationDate);
      if (!expirationValidation.valid) {
        setError(expirationValidation.message || 'Data de validade inválida');
        return false;
      }
      if (!cardData.securityCode || cardData.securityCode.length < 3 || cardData.securityCode.length > 4) {
        setError('Código de segurança inválido (CVV deve ter 3 ou 4 dígitos)');
        return false;
      }
    }

    // Validar CPF com algoritmo de validação
    const cpfDigits = cardData.customerIdentity.replace(/\D/g, '');
    if (!cpfDigits || cpfDigits.length !== 11 || !validateCPF(cardData.customerIdentity)) {
      setError('CPF inválido. Por favor, verifique o número do CPF.');
      return false;
    }
    return true;
  };

  const processPayment = async () => {
    if (!validateForm()) return;

    setError(null);
    setIsProcessing(true);

    try {
      const [expiryMonth, expiryYear] = cardData.expirationDate.split('/');

      // Montar payload no formato esperado pelo backend (PurchaseExtraCreditsRQ)
      // O backend calcula o valor automaticamente baseado na quantidade de créditos
      const paymentData: any = {
        creditsAmount: creditsAmount, // Quantidade de créditos (não o valor)
        cardNumber: useNewCard ? cardData.cardNumber.replace(/\s/g, '') : '',
        cardHolder: useNewCard ? cardData.cardHolder : '',
        expirationDate: useNewCard ? cardData.expirationDate : '',
        securityCode: useNewCard ? cardData.securityCode : '',
        brand: 'visa', // TODO: Detectar bandeira automaticamente
        customerName: cardData.customerName,
        customerEmail: cardData.customerEmail,
        customerIdentity: cardData.customerIdentity.replace(/\D/g, ''),
        customerIdentityType: 'CPF',
        // Se usar cartão salvo, enviar o ID do cartão (o backend busca o token)
        creditCardToken: useNewCard ? undefined : selectedCardId
      };

      const response = await api.post<{ success: boolean; message?: string }>('/Payment/PurchaseExtraCredits', paymentData);

      if (response.success) {
        setShowPaymentDialog(false);
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(response.message || 'Erro ao processar pagamento');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao processar pagamento');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      await api.delete(`/Payment/DeleteSavedCard/${cardId}`, { auth: true });
      loadSavedCards();
    } catch (error) {
      console.error('Erro ao deletar cartão:', error);
    }
  };

  const calculatePrice = (credits: number) => {
    if (credits <= 100) return 29.90;
    if (credits <= 500) return 129.90;
    if (credits <= 1000) return 229.90;
    return 499.90;
  };

  const calculateDiscount = (credits: number) => {
    const packageData = creditPackages.find(pkg => pkg.credits === credits);
    if (packageData) {
      return Math.round(((packageData.originalPrice - packageData.price) / packageData.originalPrice) * 100);
    }
    return 0;
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
    }}>
      {showSuccess && (
        <Fade in={showSuccess}>
          <Alert
            severity="success"
            sx={{
              position: 'fixed',
              top: 20,
              right: 20,
              zIndex: 1000,
              borderRadius: 3,
              boxShadow: theme.shadows[8],
            }}
            onClose={() => setShowSuccess(false)}
          >
            <AlertTitle>Compra realizada com sucesso!</AlertTitle>
            {creditsAmount} créditos foram adicionados à sua conta.
          </Alert>
        </Fade>
      )}

      {/* Header */}
      <Paper elevation={0} sx={{ position: 'sticky', top: 0, zIndex: 10, borderRadius: 0, background: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', py: 3, gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.primary', backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ backgroundColor: theme.palette.primary.main, width: 48, height: 48 }}>
                <ShoppingCartIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', background: 'linear-gradient(45deg, #667eea, #764ba2)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Comprar Créditos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Escolha seu plano e continue usando a Lia Talk
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 6, textAlign: 'center', background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', borderRadius: 4, p: 4, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Potencialize sua IA
            </Typography>
            <Typography variant="h5" color="rgba(255, 255, 255, 0.9)" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
              Escolha o pacote ideal e desbloqueie todo o potencial da Lia Talk
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip icon={<SecurityIcon />} label="Pagamento 100% Seguro" sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }} />
              <Chip icon={<SpeedIcon />} label="Ativação Instantânea" sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }} />
              <Chip icon={<SupportIcon />} label="Suporte 24/7" sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }} />
            </Box>
          </Box>
        </Fade>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4, alignItems: 'flex-start' }}>
          {/* Credit Packages */}
          <Box sx={{ flex: { xs: 1, lg: 2 } }}>
            <Slide in timeout={1000} direction="up">
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white', textAlign: 'center' }}>
                  Escolha seu pacote
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                  {creditPackages.map((pkg, index) => (
                    <Zoom in timeout={1200 + index * 200} key={pkg.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: selectedPackage === pkg.id ? 3 : 1,
                          borderColor: selectedPackage === pkg.id ? pkg.color : 'rgba(255, 255, 255, 0.2)',
                          position: 'relative',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: selectedPackage === pkg.id
                            ? `linear-gradient(135deg, ${alpha(pkg.color, 0.15)} 0%, ${alpha(pkg.color, 0.1)} 30%, #f8fafc 70%, #ffffff 100%)`
                            : 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: selectedPackage === pkg.id ? 'none' : 'blur(20px)',
                          boxShadow: selectedPackage === pkg.id
                            ? `0 8px 32px ${alpha(pkg.color, 0.25)}, 0 0 0 1px ${alpha(pkg.color, 0.1)}, 0 0 20px ${alpha(pkg.color, 0.15)}`
                            : '0 4px 16px rgba(0, 0, 0, 0.1)',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: `0 20px 40px ${alpha(pkg.color, 0.3)}`,
                            borderColor: pkg.color,
                          },
                        }}
                        onClick={() => handlePackageSelect(pkg)}
                      >
                        {pkg.popular && (
                          <Chip
                            label="Mais Popular"
                            sx={{
                              position: 'absolute',
                              top: -12,
                              right: 20,
                              fontWeight: 'bold',
                              background: `linear-gradient(45deg, ${pkg.color}, ${alpha(pkg.color, 0.8)})`,
                              color: 'white',
                              boxShadow: `0 4px 12px ${alpha(pkg.color, 0.4)}`,
                              zIndex: 1,
                            }}
                          />
                        )}

                        <CardContent sx={{ p: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                          <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, background: `linear-gradient(135deg, ${pkg.color} 0%, ${alpha(pkg.color, 0.8)} 100%)`, boxShadow: `0 8px 24px ${alpha(pkg.color, 0.3)}` }}>
                            {pkg.icon}
                          </Avatar>

                          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: selectedPackage === pkg.id ? pkg.color : 'text.primary' }}>
                            {pkg.name}
                          </Typography>

                          <Typography variant="h3" sx={{ fontWeight: 'bold', color: pkg.color, mb: 1, background: `linear-gradient(45deg, ${pkg.color}, ${alpha(pkg.color, 0.7)})`, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            {pkg.credits}
                          </Typography>

                          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            créditos
                          </Typography>

                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: pkg.color, display: 'inline' }}>
                              R$ {pkg.price.toFixed(2).replace('.', ',')}
                            </Typography>
                            <Typography variant="h6" sx={{ textDecoration: 'line-through', color: 'text.secondary', ml: 1, display: 'inline' }}>
                              R$ {pkg.originalPrice.toFixed(2).replace('.', ',')}
                            </Typography>
                            <Chip
                              label={`${calculateDiscount(pkg.credits)}% OFF`}
                              size="small"
                              sx={{ ml: 1, backgroundColor: alpha('#4CAF50', 0.1), color: '#4CAF50', fontWeight: 'bold' }}
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {pkg.description}
                          </Typography>

                          <Box sx={{ mb: 3 }}>
                            {pkg.features.map((feature, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
                                <CheckCircleIcon sx={{ fontSize: '1rem', color: pkg.color, mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          {selectedPackage === pkg.id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', background: `linear-gradient(135deg, ${pkg.color} 0%, ${alpha(pkg.color, 0.8)} 100%)`, borderRadius: 3, py: 1.5, px: 3, boxShadow: `0 4px 16px ${alpha(pkg.color, 0.4)}`, border: `2px solid ${alpha(pkg.color, 0.3)}` }}>
                              <CheckCircleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Selecionado
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Zoom>
                  ))}
                </Box>

                {/* Custom Amount */}
                <Slide in timeout={1600} direction="up">
                  <Card sx={{ mt: 4, background: selectedPackage === 'custom' ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 30%, #f8fafc 70%, #ffffff 100%)` : 'rgba(255, 255, 255, 0.95)', backdropFilter: selectedPackage === 'custom' ? 'none' : 'blur(20px)', border: selectedPackage === 'custom' ? 3 : 1, borderColor: selectedPackage === 'custom' ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.2)', boxShadow: selectedPackage === 'custom' ? `0 8px 32px ${alpha(theme.palette.primary.main, 0.25)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}, 0 0 20px ${alpha(theme.palette.primary.main, 0.15)}` : '0 4px 16px rgba(0, 0, 0, 0.1)', position: 'relative' }}>
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar sx={{ backgroundColor: theme.palette.primary.main, mr: 2 }}>
                          <LocalOfferIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Quantidade personalizada
                        </Typography>
                      </Box>
                      <TextField
                        label="Quantidade de créditos"
                        type="number"
                        value={creditsAmount}
                        onChange={handleCustomAmountChange}
                        inputProps={{ min: 1, max: 10000 }}
                        sx={{ width: '100%', maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        helperText="Digite a quantidade desejada de créditos"
                      />

                      {selectedPackage === 'custom' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3, color: 'white', background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`, borderRadius: 3, py: 1.5, px: 3, boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`, border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                          <CheckCircleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Selecionado
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Slide>
              </Box>
            </Slide>
          </Box>

          {/* Order Summary */}
          <Box sx={{ flex: { xs: 1, lg: 1 }, minWidth: { lg: 400 } }}>
            <Slide in timeout={1400} direction="left">
              <Paper elevation={0} sx={{ p: 4, position: 'sticky', top: 100, borderRadius: 4, background: theme.palette.mode === 'dark' ? 'rgba(36, 41, 56, 0.95) !important' : 'rgba(255, 255, 255, 0.95) !important', backdropFilter: 'blur(20px)', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)', boxShadow: theme.palette.mode === 'dark' ? '0 20px 40px rgba(0, 0, 0, 0.3)' : '0 20px 40px rgba(0, 0, 0, 0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: theme.palette.primary.main, mr: 2 }}>
                    <PaymentIcon />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Resumo do Pedido
                  </Typography>
                </Box>

                <Divider sx={{ my: 3, borderColor: alpha(theme.palette.primary.main, 0.2) }} />

                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                    <Typography variant="body1" color="text.secondary">Créditos:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {creditsAmount}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Valor unitário:</Typography>
                    <Typography variant="body2">
                      R$ {(calculatePrice(creditsAmount) / creditsAmount).toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>

                  {calculateDiscount(creditsAmount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 2, backgroundColor: alpha('#4CAF50', 0.1), borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">Desconto:</Typography>
                      <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                        {calculateDiscount(creditsAmount)}% OFF
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2, borderColor: alpha(theme.palette.primary.main, 0.2) }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.1), borderRadius: 3, border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      Total:
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      R$ {calculatePrice(creditsAmount).toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePurchase}
                  disabled={isProcessing || creditsAmount <= 0}
                  startIcon={isProcessing ? undefined : <ShoppingCartIcon />}
                  sx={{
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Finalizar Compra'}
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <SecurityIcon sx={{ fontSize: '1rem', color: '#4CAF50', mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Pagamento 100% seguro via Asaas
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Processado instantaneamente
                  </Typography>
                </Box>
              </Paper>
            </Slide>
          </Box>
        </Box>
      </Container>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => !isProcessing && setShowPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCardIcon color="primary" />
            <Typography variant="h6">Dados de Pagamento</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Seleção de cartão salvo ou novo */}
          {savedCards.length > 0 && (
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel component="legend">Forma de pagamento</FormLabel>
              <RadioGroup 
                value={useNewCard ? 'new' : 'saved'} 
                onChange={(e) => {
                  const newValue = e.target.value === 'new';
                  setUseNewCard(newValue);
                  if (!newValue && savedCards.length > 0) {
                    // Se escolher usar cartão salvo, selecionar o primeiro ou padrão
                    const defaultCard = savedCards.find((c: SavedCreditCard) => c.isDefault);
                    setSelectedCardId(defaultCard ? defaultCard.id : savedCards[0].id);
                  }
                }}
              >
                <FormControlLabel value="saved" control={<Radio />} label={`Usar cartão salvo (${savedCards.length} disponível${savedCards.length > 1 ? 'eis' : ''})`} />
                <FormControlLabel value="new" control={<Radio />} label="Usar novo cartão" />
              </RadioGroup>
            </FormControl>
          )}

          {/* Lista de cartões salvos */}
          {!useNewCard && savedCards.length > 0 && (
            <List>
              {savedCards.map((card) => (
                <ListItem
                  key={card.id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleDeleteCard(card.id)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                  sx={{
                    border: selectedCardId === card.id ? 2 : 1,
                    borderColor: selectedCardId === card.id ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    mb: 1,
                    cursor: 'pointer'
                  }}
                        onClick={() => {
                          setSelectedCardId(card.id);
                          // Preencher automaticamente os dados do cliente se disponíveis
                          if (card.customerCpfCnpj || card.customerName || card.customerEmail) {
                            setCardData(prev => ({
                              ...prev,
                              customerIdentity: card.customerCpfCnpj || prev.customerIdentity,
                              customerName: card.customerName || prev.customerName,
                              customerEmail: card.customerEmail || prev.customerEmail,
                            }));
                          }
                        }}
                >
                  <Radio checked={selectedCardId === card.id} />
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>{card.brand}</Typography>
                        <Typography>•••• {card.last4Digits}</Typography>
                        {card.isDefault && <Chip label="Padrão" size="small" color="primary" />}
                      </Box>
                    }
                    secondary={`${card.holderName} - Validade: ${card.expiryMonth}/${card.expiryYear}`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Formulário de novo cartão */}
          {useNewCard && (
            <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
              <TextField 
                fullWidth 
                label="Número do Cartão" 
                value={cardData.cardNumber} 
                onChange={(e) => handleInputChange('cardNumber', e.target.value)} 
                placeholder="1234 5678 9012 3456" 
                inputProps={{ maxLength: 19 }}
                error={cardData.cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardData.cardNumber)}
                helperText={cardData.cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardData.cardNumber) 
                  ? 'Número do cartão inválido' 
                  : ''}
              />
              <TextField fullWidth label="Nome do Portador" value={cardData.cardHolder} onChange={(e) => handleInputChange('cardHolder', e.target.value)} placeholder="Nome como está no cartão" />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Validade" 
                  value={cardData.expirationDate} 
                  onChange={(e) => handleInputChange('expirationDate', e.target.value)} 
                  placeholder="MM/AA" 
                  inputProps={{ maxLength: 5 }}
                  error={cardData.expirationDate.length > 0 && !validateExpirationDate(cardData.expirationDate).valid}
                  helperText={cardData.expirationDate.length > 0 && !validateExpirationDate(cardData.expirationDate).valid 
                    ? validateExpirationDate(cardData.expirationDate).message 
                    : 'Formato: MM/AA'}
                />
                <TextField fullWidth label="CVV" value={cardData.securityCode} onChange={(e) => handleInputChange('securityCode', e.target.value)} placeholder="123" inputProps={{ maxLength: 4 }} />
              </Box>
              {/* Nota: O cartão será salvo automaticamente após o pagamento bem-sucedido */}
              <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="black">
                  💳 Este cartão será salvo automaticamente após o pagamento para uso em futuras compras
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <TextField fullWidth label="CPF do Portador" value={cardData.customerIdentity} onChange={(e) => handleInputChange('customerIdentity', e.target.value)} placeholder="000.000.000-00" inputProps={{ maxLength: 14 }} sx={{ mb: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button onClick={processPayment} variant="contained" disabled={isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : `Pagar R$ ${calculatePrice(creditsAmount).toFixed(2).replace('.', ',')}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComprarCreditosPage;
